// /chatbot/settings — the whole nested config form. This screen is the only
// writer of /chatbot/settings; the overview screen flips one field of it.

import { useCallback, useEffect, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import { Badge, Banner, Field, Menu, SkeletonRows, Spinner, Tabs, Tag, Toggle, useToast } from "../ui";
import { ErrorBanner, errMessage, type MessageButton, type UserLite, type UsersResponse } from "./shared";
import { WikiSourcesPanel } from "./knowledge_panel";
import {
  AI_PROVIDERS,
  BOXX_AUTH_TEST_EMAIL,
  DAYS,
  PII_PATTERNS,
  SETTINGS_TABS,
  SettingRow,
  normaliseSettings,
  type BackfillResponse,
  type ChatbotSettingsForm,
  type SettingsGetResponse,
  type SettingsUpdate,
} from "./settings_model";

export function ChatbotSettingsView() {
  const { can, me } = useSession();
  const toast = useToast();
  const canWrite = can("settings.chatbot", "write");

  const [tab, setTab] = useState("messages");
  const [form, setForm] = useState<ChatbotSettingsForm>(() => normaliseSettings(undefined));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<UserLite[]>([]);

  // Write-only field: typed once, sent once, then cleared.
  const [apiKey, setApiKey] = useState("");

  const [boxxPhone, setBoxxPhone] = useState("+918826355263");
  const [boxxTesting, setBoxxTesting] = useState(false);
  const [boxxResult, setBoxxResult] = useState("");

  const [backfilling, setBackfilling] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState("");

  const patch = useCallback((next: Partial<ChatbotSettingsForm>) => {
    setForm((f) => ({ ...f, ...next }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [settings, userList] = await Promise.all([
        http.get<SettingsGetResponse>("/chatbot/settings"),
        http.get<UsersResponse>("/users").catch(() => ({ users: [] }) as UsersResponse),
      ]);
      setForm(normaliseSettings(settings.settings));
      setUsers((userList.users ?? []).filter((u) => u.is_active !== false));
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (payload: SettingsUpdate, done: string) => {
    setSaving(true);
    try {
      await http.put("/chatbot/settings", payload);
      toast.push(done, "good");
      return true;
    } catch (err) {
      toast.push(errMessage(err, "Failed to save the chatbot settings"), "critical");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveMessages = async () => {
    if (form.greeting_buttons.some((b) => !b.title.trim())) {
      toast.push("Every greeting button needs a title", "critical");
      return;
    }
    if (form.fallback_buttons.some((b) => !b.title.trim())) {
      toast.push("Every fallback button needs a title", "critical");
      return;
    }
    await save(
      {
        greeting_message: form.greeting_message,
        greeting_buttons: form.greeting_buttons.filter((b) => b.title.trim()),
        fallback_message: form.fallback_message,
        fallback_buttons: form.fallback_buttons.filter((b) => b.title.trim()),
        disclaimer_message: form.disclaimer_message,
        pii_detection_enabled: true,
        pii_blocked_message: form.pii_blocked_message,
        pii_log_detections: true,
        nps_survey_message: form.nps_survey_message,
        nps_followup_detractor: form.nps_followup_detractor,
        nps_followup_passive: form.nps_followup_passive,
        nps_followup_promoter: form.nps_followup_promoter,
        nps_thank_you_message: form.nps_thank_you_message,
        session_timeout_minutes: form.session_timeout_minutes,
        boxx_auth_enabled: form.boxx_auth_enabled,
      },
      "Message settings saved"
    );
  };

  const saveAgents = () =>
    save(
      {
        allow_agent_queue_pickup: form.allow_agent_queue_pickup,
        assign_to_same_agent: form.assign_to_same_agent,
        agent_current_conversation_only: form.agent_current_conversation_only,
      },
      "Agent settings saved"
    );

  const saveHours = () =>
    save(
      {
        business_hours_enabled: form.business_hours_enabled,
        business_hours: form.business_hours,
        out_of_hours_message: form.out_of_hours_message,
        allow_automated_outside_hours: form.allow_automated_outside_hours,
      },
      "Business hours saved"
    );

  const saveSLA = () =>
    save(
      {
        sla_enabled: form.sla_enabled,
        sla_response_minutes: form.sla_response_minutes,
        sla_resolution_minutes: form.sla_resolution_minutes,
        sla_escalation_minutes: form.sla_escalation_minutes,
        sla_auto_close_hours: form.sla_auto_close_hours,
        sla_auto_close_message: form.sla_auto_close_message,
        sla_warning_message: form.sla_warning_message,
        sla_escalation_notify_ids: form.sla_escalation_notify_ids,
        client_reminder_enabled: form.client_reminder_enabled,
        client_reminder_minutes: form.client_reminder_minutes,
        client_reminder_message: form.client_reminder_message,
        client_auto_close_minutes: form.client_auto_close_minutes,
        client_auto_close_message: form.client_auto_close_message,
      },
      "SLA settings saved"
    );

  const saveAI = async () => {
    const payload: SettingsUpdate = {
      ai_enabled: form.ai_enabled,
      ai_provider: form.ai_provider,
      ai_model: form.ai_model,
      ai_max_tokens: form.ai_max_tokens,
    };
    if (apiKey) payload.ai_api_key = apiKey;
    if (await save(payload, "AI settings saved")) setApiKey("");
  };

  const runBoxxAuthTest = async () => {
    if (!form.boxx_auth_available) {
      toast.push("BOXX authentication is not configured on this deployment", "warning");
      return;
    }
    setBoxxTesting(true);
    setBoxxResult("");
    try {
      const res = await http.post<unknown>("/chatbot/boxx-auth-test", boxxPhone.trim() ? { phone: boxxPhone.trim() } : {});
      setBoxxResult(JSON.stringify(res, null, 2));
    } catch (err) {
      toast.push(errMessage(err, "BOXX auth test failed"), "critical");
    } finally {
      setBoxxTesting(false);
    }
  };

  const backfill = async (lang: string) => {
    setBackfilling(true);
    setBackfillMessage("");
    try {
      const res = await http.post<BackfillResponse>(`/chatbot/flows/translations/backfill${qs({ lang })}`);
      const msg = `Backfill started for ${res.flow_count ?? 0} flows (${lang}).`;
      setBackfillMessage(msg);
      toast.push(msg, "good");
    } catch (err) {
      toast.push(errMessage(err, "Failed to start the translation backfill"), "critical");
    } finally {
      setBackfilling(false);
    }
  };

  const addButton = (key: "greeting_buttons" | "fallback_buttons") => {
    const list = form[key];
    if (list.length >= 10) {
      toast.push("A message can carry at most 10 buttons", "warning");
      return;
    }
    patch({ [key]: [...list, { id: `btn_${Date.now()}`, title: "" }] } as Partial<ChatbotSettingsForm>);
  };

  const setButton = (key: "greeting_buttons" | "fallback_buttons", index: number, next: Partial<MessageButton>) => {
    const list = form[key].map((b, i) => (i === index ? { ...b, ...next } : b));
    patch({ [key]: list } as Partial<ChatbotSettingsForm>);
  };

  const removeButton = (key: "greeting_buttons" | "fallback_buttons", index: number) => {
    patch({ [key]: form[key].filter((_, i) => i !== index) } as Partial<ChatbotSettingsForm>);
  };

  const escalationUsers = users.filter((u) => form.sla_escalation_notify_ids.includes(u.id));
  const unselectedUsers = users.filter((u) => !form.sla_escalation_notify_ids.includes(u.id));
  const models = AI_PROVIDERS.find((p) => p.value === form.ai_provider)?.models ?? [];
  const showBoxxTest = (me?.email ?? "").toLowerCase() === BOXX_AUTH_TEST_EMAIL;

  const SaveBar = ({ onSave }: { onSave: () => void }) => (
    <div className="card-foot" style={{ display: "flex", justifyContent: "flex-end" }}>
      <button className="btn primary" disabled={!canWrite || saving} onClick={onSave}>
        {saving && <Spinner />} Save changes
      </button>
    </div>
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Chatbot settings</h1>
          <div className="sub">Messages, handover rules, business hours, SLA, model and knowledge base.</div>
        </div>
      </div>

      <ErrorBanner error={loadError} what="Could not load the chatbot settings" />
      {!canWrite && (
        <Banner tone="info">You can read these settings but not change them (needs settings.chatbot write).</Banner>
      )}

      <Tabs tabs={SETTINGS_TABS} active={tab} onChange={setTab} />

      {loading ? (
        <div className="card">
          <SkeletonRows n={8} cols={2} />
        </div>
      ) : (
        <>
          {tab === "messages" && (
            <div className="card flush">
              <div className="card-head">
                <h3>Messages</h3>
              </div>
              <div className="card-body">
                <Field label="Greeting message" hint="Sent the first time a contact writes in a new session.">
                  <textarea
                    rows={2}
                    value={form.greeting_message}
                    onChange={(e) => patch({ greeting_message: e.target.value })}
                  />
                </Field>
                <div className="toolbar">
                  <span className="strong">Quick reply buttons</span>
                  <span className="spacer" />
                  <button
                    className="btn sm"
                    disabled={form.greeting_buttons.length >= 10}
                    onClick={() => addButton("greeting_buttons")}
                  >
                    + Add button
                  </button>
                </div>
                {form.greeting_buttons.map((b, i) => (
                  <div className="row" key={b.id || i} style={{ marginBottom: "var(--sp-2)" }}>
                    <input
                      value={b.title}
                      maxLength={20}
                      placeholder="Button label"
                      onChange={(e) => setButton("greeting_buttons", i, { title: e.target.value })}
                    />
                    <button className="btn icon ghost" aria-label="Remove button" onClick={() => removeButton("greeting_buttons", i)}>
                      ✕
                    </button>
                  </div>
                ))}
                {form.greeting_buttons.length > 0 && (
                  <p className="muted" style={{ fontSize: 12 }}>
                    WhatsApp shows at most three buttons per message; the rest become a list.
                  </p>
                )}

                <hr className="sep" />

                <Field label="Fallback message" hint="Sent when nothing matched what the contact said.">
                  <textarea
                    rows={2}
                    value={form.fallback_message}
                    onChange={(e) => patch({ fallback_message: e.target.value })}
                  />
                </Field>
                <div className="toolbar">
                  <span className="strong">Quick reply buttons</span>
                  <span className="spacer" />
                  <button
                    className="btn sm"
                    disabled={form.fallback_buttons.length >= 10}
                    onClick={() => addButton("fallback_buttons")}
                  >
                    + Add button
                  </button>
                </div>
                {form.fallback_buttons.map((b, i) => (
                  <div className="card" key={b.id || i} style={{ marginBottom: "var(--sp-2)" }}>
                    <div className="row">
                      <input
                        value={b.title}
                        maxLength={20}
                        placeholder="Button label"
                        onChange={(e) => setButton("fallback_buttons", i, { title: e.target.value })}
                      />
                      <button
                        className="btn icon ghost"
                        aria-label="Remove button"
                        onClick={() => removeButton("fallback_buttons", i)}
                      >
                        ✕
                      </button>
                    </div>
                    <textarea
                      rows={2}
                      style={{ marginTop: "var(--sp-2)" }}
                      placeholder="Reply sent when this button is tapped"
                      value={b.response ?? ""}
                      onChange={(e) => setButton("fallback_buttons", i, { response: e.target.value })}
                    />
                  </div>
                ))}

                <hr className="sep" />

                <Field
                  label="Disclaimer message"
                  hint="Shown once before the concierge answers anything. The contact must accept it."
                >
                  <textarea
                    rows={5}
                    value={form.disclaimer_message}
                    onChange={(e) => patch({ disclaimer_message: e.target.value })}
                  />
                </Field>

                <hr className="sep" />

                <h4>PII detection</h4>
                <p className="muted" style={{ fontSize: 12 }}>
                  Always on. A message carrying any of these patterns is blocked before it reaches the model.
                </p>
                <Field label="Blocked message" hint="What the contact sees when their message was withheld.">
                  <textarea
                    rows={3}
                    value={form.pii_blocked_message}
                    onChange={(e) => patch({ pii_blocked_message: e.target.value })}
                  />
                </Field>
                <div className="chips">
                  {PII_PATTERNS.map((p) => (
                    <Badge key={p} tone="warning">
                      {p}
                    </Badge>
                  ))}
                </div>

                <hr className="sep" />

                <SettingRow
                  title="BOXX policyholder authentication"
                  hint="Look the contact up in the BOXX policy system before answering account questions."
                  checked={form.boxx_auth_enabled}
                  onChange={(v) => {
                    if (v && !form.boxx_auth_available) {
                      toast.push("BOXX authentication is not configured on this deployment", "warning");
                      return;
                    }
                    patch({ boxx_auth_enabled: v });
                  }}
                />
                {!form.boxx_auth_available && (
                  <Banner tone="warning">BOXX authentication is not configured on this deployment.</Banner>
                )}
                {showBoxxTest && (
                  <div className="card" style={{ marginBottom: "var(--sp-4)" }}>
                    <h4>BOXX auth probe</h4>
                    <p className="muted" style={{ fontSize: 12 }}>
                      Calls the partner lookup for one phone number and prints the raw answer.
                    </p>
                    <Field label="Phone number">
                      <input value={boxxPhone} onChange={(e) => setBoxxPhone(e.target.value)} placeholder="+918826355263" />
                    </Field>
                    <button
                      className="btn sm"
                      disabled={boxxTesting || !form.boxx_auth_available}
                      onClick={() => void runBoxxAuthTest()}
                    >
                      {boxxTesting ? <Spinner /> : "▷"} {boxxTesting ? "Running…" : "Run test"}
                    </button>
                    {boxxResult && <pre className="pre-block">{boxxResult}</pre>}
                  </div>
                )}

                <hr className="sep" />

                <h4>NPS survey</h4>
                <p className="muted" style={{ fontSize: 12 }}>
                  Sent after a conversation closes. The follow-up depends on the score the contact gave.
                </p>
                <Field label="Survey message">
                  <textarea rows={3} value={form.nps_survey_message} onChange={(e) => patch({ nps_survey_message: e.target.value })} />
                </Field>
                <Field label="Follow-up — detractor (0-6)">
                  <textarea
                    rows={2}
                    value={form.nps_followup_detractor}
                    onChange={(e) => patch({ nps_followup_detractor: e.target.value })}
                  />
                </Field>
                <Field label="Follow-up — passive (7-8)">
                  <textarea
                    rows={2}
                    value={form.nps_followup_passive}
                    onChange={(e) => patch({ nps_followup_passive: e.target.value })}
                  />
                </Field>
                <Field label="Follow-up — promoter (9-10)">
                  <textarea
                    rows={2}
                    value={form.nps_followup_promoter}
                    onChange={(e) => patch({ nps_followup_promoter: e.target.value })}
                  />
                </Field>
                <Field label="Thank-you message">
                  <textarea
                    rows={2}
                    value={form.nps_thank_you_message}
                    onChange={(e) => patch({ nps_thank_you_message: e.target.value })}
                  />
                </Field>

                <hr className="sep" />

                <Field label="Session timeout (minutes)" hint="A quiet conversation is closed after this long.">
                  <input
                    type="number"
                    min={5}
                    max={120}
                    style={{ width: 140 }}
                    value={form.session_timeout_minutes}
                    onChange={(e) => patch({ session_timeout_minutes: Number(e.target.value) })}
                  />
                </Field>

                <hr className="sep" />

                <h4>Flow translations</h4>
                <p className="muted" style={{ fontSize: 12 }}>
                  Fill in missing translations for every published flow. Runs in the background.
                </p>
                <div className="toolbar">
                  <button className="btn sm" disabled={!canWrite || backfilling} onClick={() => void backfill("hi")}>
                    {backfilling && <Spinner />} Backfill Hindi
                  </button>
                  <button className="btn sm" disabled={!canWrite || backfilling} onClick={() => void backfill("hing")}>
                    {backfilling && <Spinner />} Backfill Hinglish
                  </button>
                  {backfillMessage && <span className="muted">{backfillMessage}</span>}
                </div>
              </div>
              <SaveBar onSave={() => void saveMessages()} />
            </div>
          )}

          {tab === "agents" && (
            <div className="card flush">
              <div className="card-head">
                <h3>Agent handover</h3>
              </div>
              <div className="card-body">
                <SettingRow
                  title="Allow queue pickup"
                  hint="Agents can pull the next waiting transfer themselves instead of waiting to be assigned."
                  checked={form.allow_agent_queue_pickup}
                  onChange={(v) => patch({ allow_agent_queue_pickup: v })}
                />
                <SettingRow
                  title="Assign to the same agent"
                  hint="A returning contact goes back to whoever handled them last, when that agent is available."
                  checked={form.assign_to_same_agent}
                  onChange={(v) => patch({ assign_to_same_agent: v })}
                />
                <SettingRow
                  title="Current conversation only"
                  hint="An agent sees only the conversation they are handling, not the contact's whole history."
                  checked={form.agent_current_conversation_only}
                  onChange={(v) => patch({ agent_current_conversation_only: v })}
                />
              </div>
              <SaveBar onSave={() => void saveAgents()} />
            </div>
          )}

          {tab === "hours" && (
            <div className="card flush">
              <div className="card-head">
                <h3>Business hours</h3>
              </div>
              <div className="card-body">
                <SettingRow
                  title="Enable business hours"
                  hint="Outside these hours the concierge answers with the out-of-hours message."
                  checked={form.business_hours_enabled}
                  onChange={(v) => patch({ business_hours_enabled: v })}
                />

                {form.business_hours_enabled && (
                  <>
                    <div className="card" style={{ marginBottom: "var(--sp-4)" }}>
                      {form.business_hours.map((h, i) => (
                        <div className="hours-row" key={h.day}>
                          <Toggle
                            checked={h.enabled}
                            onChange={(v) =>
                              patch({
                                business_hours: form.business_hours.map((x, xi) =>
                                  xi === i ? { ...x, enabled: v } : x
                                ),
                              })
                            }
                          />
                          <span className={h.enabled ? "strong" : "muted"} style={{ width: 96 }}>
                            {DAYS[h.day]}
                          </span>
                          <input
                            type="time"
                            style={{ width: 118 }}
                            disabled={!h.enabled}
                            value={h.start_time}
                            onChange={(e) =>
                              patch({
                                business_hours: form.business_hours.map((x, xi) =>
                                  xi === i ? { ...x, start_time: e.target.value } : x
                                ),
                              })
                            }
                          />
                          <span className="muted">to</span>
                          <input
                            type="time"
                            style={{ width: 118 }}
                            disabled={!h.enabled}
                            value={h.end_time}
                            onChange={(e) =>
                              patch({
                                business_hours: form.business_hours.map((x, xi) =>
                                  xi === i ? { ...x, end_time: e.target.value } : x
                                ),
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>

                    <Field label="Out-of-hours message">
                      <textarea
                        rows={2}
                        value={form.out_of_hours_message}
                        onChange={(e) => patch({ out_of_hours_message: e.target.value })}
                      />
                    </Field>

                    <SettingRow
                      title="Allow automated replies outside hours"
                      hint="Flows and AI replies keep running; only agent handover waits for the next open hour."
                      checked={form.allow_automated_outside_hours}
                      onChange={(v) => patch({ allow_automated_outside_hours: v })}
                    />
                  </>
                )}
              </div>
              <SaveBar onSave={() => void saveHours()} />
            </div>
          )}

          {tab === "sla" && (
            <div className="card flush">
              <div className="card-head">
                <h3>SLA and inactivity</h3>
              </div>
              <div className="card-body">
                <SettingRow
                  title="Enable SLA tracking"
                  hint="Track response and resolution deadlines on every transfer, and escalate when they slip."
                  checked={form.sla_enabled}
                  onChange={(v) => patch({ sla_enabled: v })}
                />

                {form.sla_enabled && (
                  <>
                    <div className="grid c2">
                      <Field label="Response time (minutes)" hint="How long a transfer may wait for a first reply.">
                        <input
                          type="number"
                          min={1}
                          max={1440}
                          value={form.sla_response_minutes}
                          onChange={(e) => patch({ sla_response_minutes: Number(e.target.value) })}
                        />
                      </Field>
                      <Field label="Escalation time (minutes)" hint="When to notify the escalation contacts.">
                        <input
                          type="number"
                          min={1}
                          max={1440}
                          value={form.sla_escalation_minutes}
                          onChange={(e) => patch({ sla_escalation_minutes: Number(e.target.value) })}
                        />
                      </Field>
                      <Field label="Resolution time (minutes)" hint="Target for closing the conversation.">
                        <input
                          type="number"
                          min={1}
                          max={10080}
                          value={form.sla_resolution_minutes}
                          onChange={(e) => patch({ sla_resolution_minutes: Number(e.target.value) })}
                        />
                      </Field>
                      <Field label="Auto close after (hours)" hint="An untouched transfer closes itself after this.">
                        <input
                          type="number"
                          min={1}
                          max={168}
                          value={form.sla_auto_close_hours}
                          onChange={(e) => patch({ sla_auto_close_hours: Number(e.target.value) })}
                        />
                      </Field>
                    </div>

                    <Field label="Auto close message">
                      <textarea
                        rows={2}
                        value={form.sla_auto_close_message}
                        onChange={(e) => patch({ sla_auto_close_message: e.target.value })}
                      />
                    </Field>
                    <Field label="Customer warning message">
                      <textarea
                        rows={2}
                        value={form.sla_warning_message}
                        onChange={(e) => patch({ sla_warning_message: e.target.value })}
                      />
                    </Field>

                    <hr className="sep" />

                    <div className="toolbar">
                      <div className="text">
                        <div className="strong">Escalation contacts</div>
                        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                          Notified when a transfer passes its escalation time.
                        </p>
                      </div>
                      <span className="spacer" />
                      <Menu
                        items={unselectedUsers.map((u) => ({
                          label: u.full_name,
                          onSelect: () =>
                            patch({ sla_escalation_notify_ids: [...form.sla_escalation_notify_ids, u.id] }),
                        }))}
                        trigger={
                          <button className="btn sm" disabled={unselectedUsers.length === 0}>
                            + Add user
                          </button>
                        }
                      />
                    </div>
                    <div className="chips">
                      {escalationUsers.length === 0 && <span className="muted">No users selected.</span>}
                      {escalationUsers.map((u) => (
                        <Tag
                          key={u.id}
                          label={u.full_name}
                          onRemove={() =>
                            patch({
                              sla_escalation_notify_ids: form.sla_escalation_notify_ids.filter((id) => id !== u.id),
                            })
                          }
                        />
                      ))}
                    </div>
                  </>
                )}

                <hr className="sep" />

                <SettingRow
                  title="Client inactivity reminders"
                  hint="Nudge a quiet contact, then close the conversation if they never come back."
                  checked={form.client_reminder_enabled}
                  onChange={(v) => patch({ client_reminder_enabled: v })}
                />

                {form.client_reminder_enabled && (
                  <>
                    <div className="grid c2">
                      <Field label="Reminder after (minutes)" hint="Silence from the contact before the nudge.">
                        <input
                          type="number"
                          min={1}
                          max={1440}
                          value={form.client_reminder_minutes}
                          onChange={(e) => patch({ client_reminder_minutes: Number(e.target.value) })}
                        />
                      </Field>
                      <Field label="Auto close after (minutes)" hint="Silence before the conversation is closed.">
                        <input
                          type="number"
                          min={1}
                          max={1440}
                          value={form.client_auto_close_minutes}
                          onChange={(e) => patch({ client_auto_close_minutes: Number(e.target.value) })}
                        />
                      </Field>
                    </div>
                    <Field label="Reminder message">
                      <textarea
                        rows={2}
                        value={form.client_reminder_message}
                        onChange={(e) => patch({ client_reminder_message: e.target.value })}
                      />
                    </Field>
                    <Field label="Auto close message">
                      <textarea
                        rows={2}
                        value={form.client_auto_close_message}
                        onChange={(e) => patch({ client_auto_close_message: e.target.value })}
                      />
                    </Field>
                  </>
                )}
              </div>
              <SaveBar onSave={() => void saveSLA()} />
            </div>
          )}

          {tab === "ai" && (
            <div className="card flush">
              <div className="card-head">
                <h3>AI responses</h3>
              </div>
              <div className="card-body">
                <SettingRow
                  title="Enable AI responses"
                  hint="Answer with a model when no flow matched."
                  checked={form.ai_enabled}
                  onChange={(v) => patch({ ai_enabled: v })}
                />

                {form.ai_enabled && (
                  <>
                    <div className="grid c2">
                      <Field label="Provider">
                        <select
                          value={form.ai_provider}
                          onChange={(e) => patch({ ai_provider: e.target.value, ai_model: "" })}
                        >
                          <option value="">Select a provider…</option>
                          {AI_PROVIDERS.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Model">
                        <select
                          value={form.ai_model}
                          disabled={!form.ai_provider}
                          onChange={(e) => patch({ ai_model: e.target.value })}
                        >
                          <option value="">Select a model…</option>
                          {models.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    <Field label="API key" hint="Stored encrypted. Leave blank to keep the key already saved.">
                      <input
                        type="password"
                        value={apiKey}
                        placeholder="Enter a new key to replace the stored one"
                        onChange={(e) => setApiKey(e.target.value)}
                      />
                    </Field>

                    <Field label="Max tokens">
                      <input
                        type="number"
                        min={100}
                        max={4000}
                        style={{ width: 140 }}
                        value={form.ai_max_tokens}
                        onChange={(e) => patch({ ai_max_tokens: Number(e.target.value) })}
                      />
                    </Field>
                  </>
                )}
              </div>
              <SaveBar onSave={() => void saveAI()} />
            </div>
          )}

          {tab === "knowledge" && <WikiSourcesPanel canWrite={canWrite} />}
        </>
      )}
    </div>
  );
}
